import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from '@angular/router';
import { Observable, Subject, of } from 'rxjs';
import { map, mergeMap, takeUntil, takeWhile } from 'rxjs/operators';
//
import { PermissionKey, PermissionList, PermissionListKey } from '@wb/core/interfaces/account-permission';
import { AccountService } from './account.service';
import { AuthService } from './auth.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class AuthGuardService implements CanActivate, OnDestroy {
  private destroyed$: Subject<void> = new Subject<void>();

  constructor(
    private authService: AuthService,
    private userService: UserService,
    private accountService: AccountService,
    private router: Router,
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.authService.isAuthenticated
      .pipe(takeUntil(this.destroyed$))
      .pipe(
        map((signedIn) => {
          if (!signedIn && (!this.router.url.includes('/auth/signUp') || !this.router.url.includes('/public'))) {
            this.router.navigate(['auth']);
            return false;
          }
          return true;
        }),
      )
      .pipe(takeWhile((signedIn) => signedIn, true))
      .pipe(
        mergeMap(() => {
          if (!route.data.expectedRole) {
            return of(true);
          }
          return this.authService.userPermission.pipe(takeUntil(this.destroyed$)).pipe(
            map((userPermissionResponse: PermissionList | undefined) => {
              if (!userPermissionResponse) {
                return false;
              }
              const expectedRole: PermissionListKey = route.data.expectedRole;
              const permissionKey: PermissionKey = route.data.permissionKey;
              const permission: any = userPermissionResponse[expectedRole];

              // Overrides permissions for users view
              if (route.data.route === 'ACCOUNT_SETTINGS' && expectedRole === 'users' && permission?.view !== true) {
                return true; // Allows access to view user (Your Profile)
              }

              if (permission?.view !== true) {
                this.router.navigate(['miscellaneous', 'error404']);
                return false;
              } else if (permissionKey && permission[permissionKey] && permission[permissionKey] !== true) {
                this.router.navigate(['miscellaneous', 'error404']);
                return false;
              }
              return true;
            }),
          );
        }),
      )
      .pipe(
        map((result) => {
          if (!result) {
            return false;
          }

          if (!this.userService.getKioskModeEnabled) {
            return true;
          }

          if (route.data.kiosk !== true) {
            this.router.navigate(['/accounts', this.accountService.currentUid, 'inventory']);
            return false;
          }
          return true;
        }),
      );
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
